import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useResourceItemStore} from '@toolify/client/src/stores/ResourceItemStore/useResourceItemStore'
import {useCollectionApiStore} from '@toolify/client/src/stores/api/CollectionApiStore/useCollectionApiStore'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {useActivityItemSocketListener} from '@toolify/client/src/composables/useActivityItemSocketListener'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {ActivityItemType} from '@toolify/server/src/models/mongoose/ActivityItemModel/enum/ActivityItemType'
import {
  ICollectionItemCreatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ICollectionItemCreatedEmitPayload'
import {
  ICollectionItemUpdatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ICollectionItemUpdatedEmitPayload'
import {
  ICollectionItemDeletedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ICollectionItemDeletedEmitPayload'
import {
  ICollectionItemsReorderedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ICollectionItemsReorderedEmitPayload'
import {ICollectionItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/ICollectionItemResponseInstance'
import {CollectionItemType} from '@toolify/server/src/models/mongoose/CollectionItemModel/enum/CollectionItemType'
import {CollectionItemBuiltInType} from '@toolify/server/src/models/mongoose/CollectionItemModel/enum/CollectionItemBuiltInType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {mapCollectionItemResponseInstance} from '@toolify/client/src/modules/api/singletons/mapCollectionItemResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {
  IResourceCollectionItemPayload,
} from '@toolify/server/src/services/CollectionService/strategies/ResourceCollectionItemTypeStrategy/types/IResourceCollectionItemTypeStrategyInterfaces'
import {findCollectionResourceItem} from '@toolify/client/src/modules/collections/functions/findCollectionResourceItem'
import {normalizeCollectionLinkUrl} from '@toolify/client/src/modules/collections/functions/normalizeCollectionLinkUrl'

export type CollectionVirtualView = null | 'recents'

const MISSING_ROW_REFETCH_DELAY_MS = 1500

function membershipKey(resourceType: ResourceType, resourceId: string) {
  return `${resourceType}:${resourceId}`
}

export const useCollectionStore = defineStore('collection', () => {
  const workspaceStore = useWorkspaceStore()
  const resourceItemStore = useResourceItemStore()
  const collectionApiStore = useCollectionApiStore()
  const localPreferenceStore = useLocalPreferenceStore()

  const collectionItemsByWorkspaceId = ref<Map<string, ICollectionItemResponseInstance[]>>(new Map())
  const currentCollectionIdByWorkspaceId = ref<Map<string, string | null>>(new Map())
  const currentVirtualViewByWorkspaceId = ref<Map<string, CollectionVirtualView>>(new Map())
  const failedWorkspaceIds = ref<Set<string>>(new Set())
  const pendingRefetchByWorkspaceId = new Map<string, ReturnType<typeof setTimeout>>()

  const allCollectionItems = computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return []
    }
    return collectionItemsByWorkspaceId.value.get(workspaceId) ?? []
  })

  const collectionItems = computed(() => {
    const resourceItems = resourceItemStore.resourceItems ?? []
    return allCollectionItems.value.filter(item => {
      if(item.collectionItemType !== CollectionItemType.Resource) {
        return true
      }
      return Boolean(findCollectionResourceItem(item, resourceItems))
    })
  })

  const hasFailedToLoad = computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    return Boolean(workspaceId && failedWorkspaceIds.value.has(workspaceId))
  })

  const currentCollectionId = computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return null
    }
    return currentCollectionIdByWorkspaceId.value.get(workspaceId) ?? null
  })

  const currentVirtualView = computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return null
    }
    return currentVirtualViewByWorkspaceId.value.get(workspaceId) ?? null
  })

  const currentCollection = computed(() => {
    if(!currentCollectionId.value) {
      return null
    }
    return collectionItems.value.find(item => item.id === currentCollectionId.value) ?? null
  })

  const rootItems = computed(() => {
    return collectionItems.value
      .filter(item => !item.parentCollectionId)
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  })

  const favouritesFolder = computed(() => {
    return collectionItems.value.find(item => item.builtInType === CollectionItemBuiltInType.Favourites) ?? null
  })

  const archiveFolder = computed(() => {
    return collectionItems.value.find(item => item.builtInType === CollectionItemBuiltInType.Archive) ?? null
  })

  const sharedRootFolders = computed(() => {
    return rootItems.value.filter(item => {
      return item.collectionItemType === CollectionItemType.Folder
        && item.builtInType !== CollectionItemBuiltInType.Favourites
        && item.builtInType !== CollectionItemBuiltInType.Archive
    })
  })

  const rootEntries = computed(() => {
    return rootItems.value.filter(item => item.collectionItemType !== CollectionItemType.Folder)
  })

  function childrenOf(parentCollectionId: string | null) {
    return collectionItems.value
      .filter(item => {
        if(parentCollectionId == null) {
          return item.parentCollectionId == null
        }
        return item.parentCollectionId != null && String(item.parentCollectionId) === String(parentCollectionId)
      })
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  }

  function descendantIdsOf(collectionItemId: string, items: ICollectionItemResponseInstance[] = allCollectionItems.value) {
    const collected: string[] = []
    let frontier = [collectionItemId]
    while(frontier.length) {
      const frontierSet = new Set(frontier)
      frontier = items
        .filter(item => item.parentCollectionId && frontierSet.has(String(item.parentCollectionId)))
        .map(item => item.id)
      collected.push(...frontier)
    }
    return collected
  }

  const visibleChildren = computed(() => {
    if(currentVirtualView.value === 'recents') {
      return []
    }
    return childrenOf(currentCollectionId.value)
  })

  const membershipParentIdsByResource = computed(() => {
    const map = new Map<string, string[]>()
    for(const item of allCollectionItems.value) {
      if(item.collectionItemType !== CollectionItemType.Resource) {
        continue
      }
      const payload = item.collectionItemTypePayload as IResourceCollectionItemPayload
      if(!payload?.resourceType || !payload.resourceId) {
        continue
      }
      const key = membershipKey(payload.resourceType, payload.resourceId)
      const parentCollectionIds = map.get(key) ?? []
      parentCollectionIds.push(item.parentCollectionId ?? '')
      map.set(key, parentCollectionIds)
    }
    return map
  })

  function isResourceInCollection(resourceType: ResourceType, resourceId: string, parentCollectionId: string | null) {
    const parentCollectionIds = membershipParentIdsByResource.value.get(membershipKey(resourceType, resourceId)) ?? []
    return parentCollectionIds.includes(parentCollectionId ?? '')
  }

  function findResourceRow(resourceType: ResourceType, resourceId: string, parentCollectionId: string | null) {
    return allCollectionItems.value.find(item => {
      if(item.collectionItemType !== CollectionItemType.Resource) {
        return false
      }
      const payload = item.collectionItemTypePayload as IResourceCollectionItemPayload
      return payload.resourceType === resourceType
        && String(payload.resourceId) === String(resourceId)
        && (item.parentCollectionId ?? null) === parentCollectionId
    }) ?? null
  }

  function setCollectionItems(workspaceId: string, items: ICollectionItemResponseInstance[]) {
    const next = new Map(collectionItemsByWorkspaceId.value)
    next.set(workspaceId, items)
    collectionItemsByWorkspaceId.value = next
  }

  function upsertCollectionItem(item: ICollectionItemResponseInstance) {
    const workspaceId = item.workspaceId
    const current = collectionItemsByWorkspaceId.value.get(workspaceId) ?? []
    const index = current.findIndex(existing => existing.id === item.id)
    const nextItems = index === -1
      ? [...current, item]
      : current.map(existing => existing.id === item.id ? item : existing)
    setCollectionItems(workspaceId, nextItems)
  }

  function applySiblingOrder(orderedIds: string[], parentCollectionId: string | null, workspaceId = workspaceStore.currentWorkspace?.id) {
    if(!workspaceId) {
      return
    }
    const current = collectionItemsByWorkspaceId.value.get(workspaceId) ?? []
    setCollectionItems(workspaceId, current.map(item => {
      const position = orderedIds.indexOf(item.id)
      if(position === -1) {
        return item
      }
      return {
        ...item,
        parentCollectionId,
        position,
      }
    }))
  }

  function leaveDeletedFolder(workspaceId: string, deletedIds: Set<string>) {
    const current = currentCollectionIdByWorkspaceId.value.get(workspaceId) ?? null
    if(!current) {
      return
    }
    const items = collectionItemsByWorkspaceId.value.get(workspaceId) ?? []
    const chain: string[] = []
    let cursor = items.find(item => item.id === current) ?? null
    while(cursor) {
      chain.push(cursor.id)
      cursor = cursor.parentCollectionId
        ? items.find(item => item.id === cursor.parentCollectionId) ?? null
        : null
    }
    const firstDeletedIndex = chain.findIndex(id => deletedIds.has(id))
    if(chain.length && firstDeletedIndex === -1) {
      return
    }
    setCurrentCollectionId(workspaceId, chain.slice(firstDeletedIndex + 1).find(id => !deletedIds.has(id)) ?? null)
  }

  function removeCollectionItems(workspaceId: string, collectionItemIds: string[]) {
    const current = collectionItemsByWorkspaceId.value.get(workspaceId) ?? []
    const deletedIds = new Set(collectionItemIds.flatMap(id => [id, ...descendantIdsOf(id, current)]))
    leaveDeletedFolder(workspaceId, deletedIds)
    setCollectionItems(workspaceId, current.filter(item => !deletedIds.has(item.id)))
  }

  function removeCollectionItem(workspaceId: string, collectionItemId: string) {
    removeCollectionItems(workspaceId, [collectionItemId])
  }

  function removeResourceRows(workspaceId: string, resourceType: ResourceType, resourceId: string) {
    const current = collectionItemsByWorkspaceId.value.get(workspaceId) ?? []
    const rowIds = current
      .filter(item => {
        if(item.collectionItemType !== CollectionItemType.Resource) {
          return false
        }
        const payload = item.collectionItemTypePayload as IResourceCollectionItemPayload
        return payload.resourceType === resourceType && String(payload.resourceId) === String(resourceId)
      })
      .map(item => item.id)
    if(rowIds.length) {
      removeCollectionItems(workspaceId, rowIds)
    }
  }

  function setCurrentCollectionId(workspaceId: string, collectionItemId: string | null) {
    const next = new Map(currentCollectionIdByWorkspaceId.value)
    next.set(workspaceId, collectionItemId)
    currentCollectionIdByWorkspaceId.value = next
    const virtualNext = new Map(currentVirtualViewByWorkspaceId.value)
    virtualNext.set(workspaceId, null)
    currentVirtualViewByWorkspaceId.value = virtualNext
    if(collectionItemId) {
      void localPreferenceStore.set(LocalPreferenceType.LastVisitedCollectionId, collectionItemId, workspaceId)
    } else {
      void localPreferenceStore.remove(LocalPreferenceType.LastVisitedCollectionId, workspaceId)
    }
  }

  function setCurrentVirtualView(workspaceId: string, view: CollectionVirtualView) {
    const virtualNext = new Map(currentVirtualViewByWorkspaceId.value)
    virtualNext.set(workspaceId, view)
    currentVirtualViewByWorkspaceId.value = virtualNext
    const collectionNext = new Map(currentCollectionIdByWorkspaceId.value)
    collectionNext.set(workspaceId, null)
    currentCollectionIdByWorkspaceId.value = collectionNext
  }

  function hasCollectionItemsLoaded(workspaceId: string) {
    return collectionItemsByWorkspaceId.value.has(workspaceId)
  }

  function setFailed(workspaceId: string, hasFailed: boolean) {
    const next = new Set(failedWorkspaceIds.value)
    if(hasFailed) {
      next.add(workspaceId)
    } else {
      next.delete(workspaceId)
    }
    failedWorkspaceIds.value = next
  }

  async function fetchCollectionItems(workspaceId: string) {
    try {
      const {collectionItems: items} = await collectionApiStore.getCollectionItems(workspaceId)
      setCollectionItems(workspaceId, items)
      setFailed(workspaceId, false)
      const lastVisited = localPreferenceStore.get(LocalPreferenceType.LastVisitedCollectionId, workspaceId)
      const hasCurrent = currentCollectionIdByWorkspaceId.value.has(workspaceId)
      if(lastVisited && !hasCurrent) {
        setCurrentCollectionId(workspaceId, items.some(item => item.id === lastVisited) ? lastVisited : null)
      }
    } catch {
      setFailed(workspaceId, true)
    }
  }

  function scheduleRefetch(workspaceId: string) {
    const pending = pendingRefetchByWorkspaceId.get(workspaceId)
    if(pending) {
      clearTimeout(pending)
    }
    pendingRefetchByWorkspaceId.set(workspaceId, setTimeout(() => {
      pendingRefetchByWorkspaceId.delete(workspaceId)
      void fetchCollectionItems(workspaceId)
    }, MISSING_ROW_REFETCH_DELAY_MS))
  }

  async function createFolder(name: string, parentCollectionId: string | null = null) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return null
    }
    const {collectionItem} = await collectionApiStore.createCollectionItem(workspaceId, {
      collectionItemType: CollectionItemType.Folder,
      collectionItemTypePayload: {
        iconNameType: null,
        particularColor: null,
      },
      name,
      parentCollectionId,
    })
    upsertCollectionItem(collectionItem)
    return collectionItem
  }

  async function createLink(name: string, url: string, parentCollectionId: string | null = null) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return null
    }
    const {collectionItem} = await collectionApiStore.createCollectionItem(workspaceId, {
      collectionItemType: CollectionItemType.Link,
      collectionItemTypePayload: {
        url: normalizeCollectionLinkUrl(url),
      },
      name,
      parentCollectionId,
    })
    upsertCollectionItem(collectionItem)
    return collectionItem
  }

  async function updateCollectionItem(
    collectionItemId: string,
    body: Parameters<typeof collectionApiStore.updateCollectionItem>[1],
  ) {
    const {collectionItem} = await collectionApiStore.updateCollectionItem(collectionItemId, body)
    upsertCollectionItem(collectionItem)
    return collectionItem
  }

  async function deleteCollectionItem(collectionItemId: string) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await collectionApiStore.deleteCollectionItem(collectionItemId)
    removeCollectionItem(workspaceId, collectionItemId)
  }

  async function setMembership(
    parentCollectionId: string | null,
    resourceType: ResourceType,
    resourceId: string,
    isMember: boolean,
    position?: number,
  ) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return null
    }
    const {collectionItem} = await collectionApiStore.updateCollectionItemMembership(workspaceId, {
      parentCollectionId,
      resourceType,
      resourceId,
      isMember,
      position,
    })
    if(isMember && collectionItem) {
      upsertCollectionItem(collectionItem)
    } else {
      const existing = findResourceRow(resourceType, resourceId, parentCollectionId)
      if(existing) {
        removeCollectionItem(workspaceId, existing.id)
      }
    }
    return collectionItem
  }

  const alwaysActive = {
    lifecycleFn() {
      return true
    },
  }

  function upsertFromSocket(item: ICollectionItemResponseInstance) {
    const known = collectionItemsByWorkspaceId.value.get(item.workspaceId)
    if(known && item.parentCollectionId && !known.some(existing => existing.id === item.parentCollectionId)) {
      return
    }
    upsertCollectionItem(item)
  }

  useSocketListener<ICollectionItemCreatedEmitPayload>(GlobalSocketEventType.CollectionItemCreated, payload => {
    if(!payload?.collectionItem) {
      return
    }
    upsertFromSocket(mapCollectionItemResponseInstance(payload.collectionItem as IToJson<ICollectionItemResponseInstance>))
  }, alwaysActive)

  useSocketListener<ICollectionItemUpdatedEmitPayload>(GlobalSocketEventType.CollectionItemUpdated, payload => {
    if(!payload?.collectionItem) {
      return
    }
    upsertFromSocket(mapCollectionItemResponseInstance(payload.collectionItem as IToJson<ICollectionItemResponseInstance>))
  }, alwaysActive)

  useSocketListener<ICollectionItemDeletedEmitPayload>(GlobalSocketEventType.CollectionItemDeleted, payload => {
    if(!payload?.workspaceId || !payload.collectionItemId) {
      return
    }
    removeCollectionItems(payload.workspaceId, payload.collectionItemIds?.length ? payload.collectionItemIds : [payload.collectionItemId])
  }, alwaysActive)

  useSocketListener<ICollectionItemsReorderedEmitPayload>(GlobalSocketEventType.CollectionItemsReordered, payload => {
    if(!payload?.workspaceId || !Array.isArray(payload.orderedIds)) {
      return
    }
    applySiblingOrder(payload.orderedIds, payload.parentCollectionId ?? null, payload.workspaceId)
  }, alwaysActive)

  useActivityItemSocketListener(ActivityItemType.ResourceItemCreated, ({payload}) => {
    const resourceItem = payload.resourceItem
    const workspaceId = resourceItem?.workspaceId
    if(!workspaceId || !hasCollectionItemsLoaded(workspaceId)) {
      return
    }
    const {resourceType, resourceId} = resourceItem.resourceEntity
    const hasRow = (collectionItemsByWorkspaceId.value.get(workspaceId) ?? []).some(item => {
      if(item.collectionItemType !== CollectionItemType.Resource) {
        return false
      }
      const rowPayload = item.collectionItemTypePayload as IResourceCollectionItemPayload
      return rowPayload.resourceType === resourceType && String(rowPayload.resourceId) === String(resourceId)
    })
    if(!hasRow) {
      scheduleRefetch(workspaceId)
    }
  }, null, alwaysActive)

  useActivityItemSocketListener(ActivityItemType.ResourceItemDeleted, ({payload}) => {
    for(const workspaceId of collectionItemsByWorkspaceId.value.keys()) {
      removeResourceRows(workspaceId, payload.resourceType, payload.resourceId)
    }
  }, null, alwaysActive)

  return {
    collectionItems,
    hasFailedToLoad,
    currentCollectionId,
    currentVirtualView,
    currentCollection,
    rootItems,
    rootEntries,
    favouritesFolder,
    archiveFolder,
    sharedRootFolders,
    visibleChildren,
    membershipParentIdsByResource,
    childrenOf,
    descendantIdsOf,
    isResourceInCollection,
    findResourceRow,
    setCurrentCollectionId,
    setCurrentVirtualView,
    hasCollectionItemsLoaded,
    fetchCollectionItems,
    createFolder,
    createLink,
    updateCollectionItem,
    applySiblingOrder,
    deleteCollectionItem,
    removeResourceRows,
    setMembership,
    upsertCollectionItem,
  }
})
